import { Link } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ArrowRight, Users, Calendar, Star, MapPin, Clock, ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { EditableText } from '../components/EditableText';
import { useAuth } from '../context/AuthContext';
import { testimonials, volunteers, impactStats, repairProcess } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Home() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [eventSettings, setEventSettings] = useState({
    event_date: 'To be announced',
    event_time: '',
    venue_name: 'Peterborough Repair Café',
    venue_url: '',
  });
  const [slides, setSlides] = useState([]);
  const [loadingSlides, setLoadingSlides] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  // TEMPORARY HIDE FLAG - Use key phrase "SHOW_COMMUNITY_SECTIONS" to restore
  const HIDE_COMMUNITY_SECTIONS = true;

  useEffect(() => {
    fetchEventSettings();
    fetchSlides();
  }, []);

  const fetchEventSettings = async () => {
    try {
      // Cache-bust so admin schedule edits show up on next page load.
      const response = await axios.get(`${API}/event-settings/public`, {
        params: { _ts: Date.now() },
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (response.data) setEventSettings(response.data);
    } catch (error) {
      console.error('Error fetching event settings:', error);
    }
  };

  const fetchSlides = async () => {
    try {
      const response = await axios.get(`${API}/slideshow/public`);
      setSlides(response.data || []);
    } catch (error) {
      setSlides([]);
    } finally {
      setLoadingSlides(false);
    }
  };

  // Auto-rotate slides every 5s
  useEffect(() => {
    if (slides.length <= 1) return;
    const id = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(id);
  }, [slides.length]);

  const nextSlide = useCallback(() => {
    if (!slides.length) return;
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    if (!slides.length) return;
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const getIcon = (iconName) => {
    const Icon = LucideIcons[iconName];
    return Icon ? <Icon className="w-6 h-6" /> : null;
  };

  return (
    <div className="min-h-screen">
      {/* Super Admin live-edit notice — visible only to logged-in super admins */}
      {isSuperAdmin && (
        <div
          data-testid="super-admin-edit-banner"
          className="bg-emerald-600 text-white border-b-2 border-emerald-700"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-start sm:items-center gap-3">
            <div className="w-9 h-9 flex-shrink-0 rounded-full bg-emerald-700/40 flex items-center justify-center">
              <Lock className="w-5 h-5" />
            </div>
            <div className="flex-1 text-sm sm:text-base leading-snug">
              <span className="font-semibold">You are logged in as a Super Admin.</span>{' '}
              <span className="text-emerald-50">
                Editable fields on this page can be updated and changes will go live.
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/6476254/pexels-photo-6476254.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`,
          }}
        >
          <div className="absolute inset-0 bg-[#193460] opacity-85"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <EditableText contentKey="home.hero.title" as="span">Fix Together, Waste Less</EditableText>
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed animate-in fade-in slide-in-from-bottom-4 duration-700 delay-150">
            <EditableText contentKey="home.hero.subtitle" as="span" multiline>
              Peterborough Repair Cafe volunteers meet on the first Saturday of every month to help repair your portable household items for free.
            </EditableText>
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300">
            <Link to="/repair-request#enquiry-form">
              <Button size="lg" data-testid="home-submit-request-btn" className="bg-[#E87B6A] hover:bg-[#d66a59] text-white font-semibold px-8 py-6 text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
                Send an enquiry
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#193460] font-semibold px-8 py-6 text-lg shadow-lg transform hover:scale-105 transition-all duration-300">
                See Our Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Next Event Banner */}
      <section className="bg-gradient-to-r from-[#193460] to-[#1e4270] py-4 border-b-4 border-[#E87B6A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 text-white">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#E87B6A]" />
              <span className="font-semibold">Next Event:</span>
              <span>{eventSettings.event_date}</span>
            </div>
            {eventSettings.event_time && (
              <>
                <div className="hidden md:block w-px h-6 bg-gray-400"></div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#E87B6A]" />
                  <span>{eventSettings.event_time}</span>
                </div>
              </>
            )}
            <div className="hidden md:block w-px h-6 bg-gray-400"></div>
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#E87B6A]" />
              {eventSettings.venue_url ? (
                <a href={eventSettings.venue_url} target="_blank" rel="noopener noreferrer" className="hover:text-[#E87B6A] transition-colors underline">
                  {eventSettings.venue_name}
                </a>
              ) : (
                <span>{eventSettings.venue_name}</span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* What is a Repair Cafe - with slideshow */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-[#193460] mb-6">
                <EditableText contentKey="home.about.title" as="span">What is a Repair Cafe?</EditableText>
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                <EditableText contentKey="home.about.p1" as="span" multiline>
                  A Repair Cafe is a free community event where volunteers with repair skills help local residents fix broken items instead of throwing them away.
                </EditableText>
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                <EditableText contentKey="home.about.p2" as="span" multiline>
                  We believe in reducing waste, sharing knowledge, and building stronger communities. Our skilled volunteers donate their time to help repair everything from toasters to bicycles, textiles to toys.
                </EditableText>
              </p>
              <div className="flex items-start space-x-4 mb-4">
                <img src="https://customer-assets.emergentagent.com/job_waste-less-hub-1/artifacts/918kjlll_image.png" alt="Repair Cafe Logo" className="w-12 h-12 rounded-full flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-[#193460] text-xl mb-2">Free Repairs</h3>
                  <p className="text-gray-600">All repair services are completely free. We're here to help, not to profit.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#193460] rounded-full flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#193460] text-xl mb-2">Community Spirit</h3>
                  <p className="text-gray-600">Meet your neighbors, share stories, and learn new skills in a friendly environment.</p>
                </div>
              </div>
            </div>

            {/* Slideshow */}
            <div className="relative" data-testid="home-slideshow">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/3] bg-gray-100">
                {slides.length > 0 ? (
                  <>
                    {slides.map((s, i) => (
                      <img
                        key={s.id}
                        src={s.url}
                        alt={s.caption || `Repair Cafe slide ${i + 1}`}
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${
                          i === currentSlide ? 'opacity-100' : 'opacity-0'
                        }`}
                        loading={i === 0 ? 'eager' : 'lazy'}
                        data-testid={`slide-image-${i}`}
                      />
                    ))}

                    {slides[currentSlide]?.caption && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-4 pt-16">
                        <p className="text-white text-sm md:text-base font-medium" data-testid="slide-caption">
                          {slides[currentSlide].caption}
                        </p>
                      </div>
                    )}

                    {slides.length > 1 && (
                      <>
                        <button
                          onClick={prevSlide}
                          aria-label="Previous slide"
                          data-testid="slide-prev-btn"
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white text-[#193460] flex items-center justify-center shadow-lg transition-transform hover:scale-110"
                        >
                          <ChevronLeft className="w-6 h-6" />
                        </button>
                        <button
                          onClick={nextSlide}
                          aria-label="Next slide"
                          data-testid="slide-next-btn"
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white text-[#193460] flex items-center justify-center shadow-lg transition-transform hover:scale-110"
                        >
                          <ChevronRight className="w-6 h-6" />
                        </button>

                        <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2">
                          {slides.map((_, i) => (
                            <button
                              key={i}
                              aria-label={`Go to slide ${i + 1}`}
                              data-testid={`slide-dot-${i}`}
                              onClick={() => setCurrentSlide(i)}
                              className={`transition-all rounded-full ${
                                i === currentSlide ? 'w-8 h-2 bg-white' : 'w-2 h-2 bg-white/60 hover:bg-white/90'
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : loadingSlides ? (
                  // Empty placeholder while we fetch — matches the gray-100 frame
                  // so the page doesn't flash a fallback image.
                  <div
                    className="absolute inset-0 w-full h-full bg-gray-100 animate-pulse"
                    data-testid="slideshow-loading"
                  />
                ) : (
                  <img
                    src="https://images.unsplash.com/photo-1770739879041-22f0dfc37301?crop=entropy&cs=srgb&fm=jpg&q=85"
                    alt="Community workshop"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#193460] mb-4">How the Repair Process Works</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Getting your item repaired is simple and straightforward. Follow these four easy steps.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {repairProcess.map((step) => (
              <Card key={step.id} className="relative border-2 border-gray-200 hover:border-[#E87B6A] transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="text-6xl font-bold text-[#E87B6A] opacity-20 absolute top-4 right-4">
                    {step.step}
                  </div>
                  <div className="relative z-10">
                    <div className="w-14 h-14 bg-[#193460] rounded-full flex items-center justify-center mb-4">
                      <span className="text-2xl font-bold text-white">{step.step}</span>
                    </div>
                    <h3 className="text-xl font-semibold text-[#193460] mb-3">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Community Impact - HIDDEN until "SHOW_COMMUNITY_SECTIONS" */}
      {!HIDE_COMMUNITY_SECTIONS && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#193460] mb-4">Our Community Impact</h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Together, we're making a real difference in Peterborough.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
              {impactStats.map((stat) => (
                <Card key={stat.id} className="text-center border-2 border-gray-200 hover:border-[#E87B6A]">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-[#E87B6A] rounded-full flex items-center justify-center mx-auto mb-4">
                      {getIcon(stat.icon)}
                    </div>
                    <div className="text-4xl font-bold text-[#193460] mb-2">{stat.number}</div>
                    <div className="text-gray-600 font-medium">{stat.label}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {!HIDE_COMMUNITY_SECTIONS && (
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#193460] mb-4">Meet Our Volunteer Team</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {volunteers.map((v) => (
                <Card key={v.id} className="text-center border-2 border-gray-200">
                  <CardContent className="p-6">
                    <Avatar className="w-24 h-24 mx-auto mb-4 bg-[#E87B6A]">
                      <AvatarFallback className="text-2xl font-bold text-white">{v.avatar}</AvatarFallback>
                    </Avatar>
                    <h3 className="text-xl font-semibold text-[#193460] mb-2">{v.name}</h3>
                    <p className="text-[#E87B6A] font-medium mb-1">{v.specialty}</p>
                    <p className="text-gray-600 text-sm">{v.experience} experience</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {!HIDE_COMMUNITY_SECTIONS && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#193460] mb-4">What Our Community Says</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8 mb-10">
              {testimonials.slice(0, 3).map((t) => (
                <Card key={t.id} className="border-2 border-gray-200">
                  <CardContent className="p-6">
                    <div className="flex items-center mb-4">
                      <Avatar className="w-12 h-12 mr-3 bg-[#193460]">
                        <AvatarFallback className="text-white font-semibold">{t.avatar}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <h4 className="font-semibold text-[#193460]">{t.name}</h4>
                        <p className="text-sm text-gray-500">{t.date}</p>
                      </div>
                    </div>
                    <div className="flex mb-3">
                      {[...Array(t.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-[#E87B6A] text-[#E87B6A]" />
                      ))}
                    </div>
                    <p className="text-gray-700 leading-relaxed">{t.comment}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-20 bg-[#193460]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Repair Instead of Replace?</h2>
          <p className="text-xl text-gray-200 mb-8 leading-relaxed">
            Join us at our next repair cafe event. Bring your broken items and leave with working ones!
          </p>
          <Link to="/repair-request#enquiry-form">
            <Button size="lg" data-testid="home-cta-submit-btn" className="bg-[#E87B6A] hover:bg-[#d66a59] text-white font-semibold px-10 py-6 text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
              Send an enquiry
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
